import { AlertTriangle, CalendarClock, ChevronRight, Headphones, Plug } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { graphql, useMutation } from "react-relay";
import { useNavigate } from "react-router";
import { cn } from "src/common/utils";
import { BlurFade } from "src/components/ui/blur-fade";
import { Button } from "src/components/ui/button";
import { ChatComposer, type ChatComposerRefType } from "src/components/ui/chat/ChatComposer";
import type { ChatAttachmentType } from "src/components/ui/chat/useChatAttachments";
import { useMe } from "src/context/MeContext";
import { getProductName, isRootlyBranded } from "src/utils/brandTheme";

import type { WelcomePageCreateConversationMutation } from "./__generated__/WelcomePageCreateConversationMutation.graphql";

const createConversationMutation = graphql`
  mutation WelcomePageCreateConversationMutation($input: SendMessageInput!) {
    sendMessage(input: $input) {
      llmConversation {
        id
      }
    }
  }
`;

interface SendMessageInputType {
  message: string;
  attachments?: { handle: string }[];
}

interface SuggestionItemType {
  title: string;
  description: React.ReactNode;
  buttonLabel: string;
  icon: React.ReactNode;
  to: string;
}

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const me = useMe();
  const chatInputRef = useRef<ChatComposerRefType>(null);
  const [inputValue, setInputValue] = useState("");
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [commitMutation, isSending] = useMutation<WelcomePageCreateConversationMutation>(createConversationMutation);

  const productName = getProductName(me.organization?.brandTheme);
  const showFeaturedWorkflows = !isRootlyBranded(me.organization?.brandTheme);

  useEffect(() => {
    chatInputRef.current?.focus();
  }, []);

  const sendMessage = useCallback(
    async (message: string, attachments: ChatAttachmentType[] = []) => {
      const trimmedMessage = message.trim();
      if (!trimmedMessage) return;

      const attachmentRefs = attachments
        .map((a) => a.handle)
        .filter((h): h is string => Boolean(h))
        .map((handle) => ({ handle }));

      const input: SendMessageInputType = {
        message: trimmedMessage,
        ...(attachmentRefs.length ? { attachments: attachmentRefs } : {}),
      };

      return new Promise<void>((resolve, reject) => {
        commitMutation({
          variables: { input },
          onCompleted: (response) => {
            const llmConversationId = response.sendMessage.llmConversation?.id;
            if (llmConversationId) {
              navigate(`/chat/c/${llmConversationId}`, {
                state: { initialMessage: trimmedMessage, isNewConversation: true },
              });
            }
            resolve();
          },
          onError: (error) => {
            reject(error);
          },
        });
      });
    },
    [commitMutation, navigate]
  );

  const handleSendMessage = useCallback(async (message: string, attachments: ChatAttachmentType[] = []) => {
    await sendMessage(message, attachments);
    setInputValue("");
  }, [sendMessage]);

  const items: SuggestionItemType[] = [
    {
      title: "Reduce alert workload and noise",
      description: <>When an alert triggers, {productName} automatically responds with an investigation, impact analysis, and recommended next step. It detects alert noise and correlation with other alerts and incidents.</>,
      buttonLabel: "Setup alert agent",
      icon: <AlertTriangle className="h-4 w-4" />,
      to: "/alerts?create=true",
    },
    {
      title: "Automate your oncall retrospective",
      description: "Schedule and customize a digest to generate oncall retrospective reports that summarize alerts, incidents, and action items on a recurring basis.",
      buttonLabel: "Setup oncall retrospective digest",
      icon: <CalendarClock className="h-4 w-4" />,
      to: "/digests?create=true&template=oncall_retrospective",
    },
    {
      title: "Bring your production context to your development environment",
      description: <>Connect {productName} MCP so your coding agent can investigate production issues directly from your IDE.</>,
      buttonLabel: `Setup ${productName} MCP`,
      icon: <Plug className="h-4 w-4" />,
      to: "/mcp",
    },
    {
      title: "Field engineering support tickets",
      description: <>When a support request or ticket comes to your team through Slack or Linear, {productName} triages and responds automatically on the thread or ticket.</>,
      buttonLabel: "Setup support agent",
      icon: <Headphones className="h-4 w-4" />,
      to: "/support/agents?create=true",
    },
  ];

  return (
    <div className={cn("flex-1 flex flex-col items-center overflow-auto px-6 pb-12 relative", showFeaturedWorkflows ? "pt-24" : "pt-48")}>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.08)_0%,rgba(0,0,0,0)_50%)] pointer-events-none" />
      <div className="w-full max-w-5xl flex flex-col items-center relative">
        <BlurFade duration={0.8}>
          <h1 className="text-3xl md:text-4xl font-normal mb-3 text-primary tracking-tight text-center">
            Welcome to {productName}
          </h1>
        </BlurFade>

        <BlurFade delay={0.1} duration={0.8}>
          <p className="text-sm md:text-base text-foreground mb-12 text-center">
            Search across your code, logs, metrics, traces, and deploys to answer questions and investigate issues
          </p>
        </BlurFade>

        <div className="w-full">
          <form className="relative rounded-md bg-popover border border-border p-1.5 shadow-sm focus-within:border-primary/50 transition-colors duration-150">
            <ChatComposer
              ref={chatInputRef}
              placeholder="Ask anything about your systems"
              onSend={handleSendMessage}
              defaultValue={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={isSending}
              sendDisabled={isSending}
              inputClassName="min-h-[44px] w-full resize-none border-0 bg-transparent py-3 text-foreground text-sm shadow-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              attachmentButtonClassName="left-1.5"
            />
          </form>
        </div>

        {showFeaturedWorkflows && (
          <BlurFade delay={0.2} duration={0.6} className="w-full flex flex-col items-center mt-16">
            <div className="w-full max-w-[52rem] h-px bg-border mb-16" />
            <h2 className="w-full max-w-[60rem] text-lg font-medium text-foreground mb-3">Featured Workflows</h2>
            <ul className="w-full max-w-[60rem] flex flex-col divide-y divide-border">
            {items.map((item, index) => {
              const isExpanded = expandedIndex === index;
              return (
                <li key={item.title}>
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    onClick={() => setExpandedIndex(isExpanded ? null : index)}
                    className="group w-full flex items-center gap-3 py-3 text-left hover:bg-accent/40 transition-colors duration-150 rounded-md px-2 -mx-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <span className="text-muted-foreground flex-shrink-0">{item.icon}</span>
                    <span className="flex-1 text-sm text-foreground">{item.title}</span>
                    <ChevronRight
                      className={cn(
                        "h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform duration-150",
                        isExpanded ? "rotate-90" : "group-hover:translate-x-0.5"
                      )}
                    />
                  </button>
                  {isExpanded && (
                    <div className="px-2 pt-2 pb-4 flex items-center gap-4">
                      <p className="text-xs text-muted-foreground leading-relaxed basis-3/5 shrink-0">{item.description}</p>
                      <div className="flex-1 flex justify-end">
                        <Button variant="primary" size="sm" onClick={() => navigate(item.to)}>
                          {item.buttonLabel}
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
            </ul>
          </BlurFade>
        )}
      </div>
    </div>
  );
};

export default WelcomePage;
